import { useEffect, useState } from "react";
import { FileText, Download, Send, RefreshCw, Eye, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

export function ReportActions({ testId, isPublished, summaryApproved, isManager }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!testId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports/${testId}`);
      setMeta(data);
    } catch (e) {
      if (e.response?.status !== 404) toast.error(formatApiError(e.response?.data?.detail) || "Could not load report");
      setMeta(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [testId]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reports/${testId}/generate`);
      setMeta(data);
      toast.success(`Report v${data.version} generated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally { setBusy(false); }
  };

  const downloadOrPreview = async () => {
    try {
      const token = localStorage.getItem("scwt_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/${testId}/pdf`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Could not download");
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error("Could not open PDF");
    }
  };

  const send = async ({ force = false } = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/reports/${testId}/send${force ? "?force=true" : ""}`
      );
      // Feb 2026 — server-side dedup guard.
      // Backend returns { duplicate_suppressed: true, seconds_since_last_send }
      // when the same report was emailed within the 60-second cooldown.
      // Show a confirm dialog so the admin can knowingly force a re-send
      // (instead of silently believing they sent duplicates).
      if (data.duplicate_suppressed) {
        const age = Math.round(data.seconds_since_last_send || 0);
        const ok = window.confirm(
          `⚠  Duplicate send blocked\n\n` +
          `This report was already emailed to ${data.to} ${age} second(s) ago.\n\n` +
          `Sending it again right now will deliver a second copy of the same email.\n\n` +
          `Click OK only if you're sure you want to re-send.`
        );
        if (ok) {
          // Re-run with the force override.
          await send({ force: true });
          return;
        }
        toast.info(`Send skipped — already emailed ${age}s ago`);
        return;
      }
      if (data.sent) toast.success(`Report sent to ${data.to}`);
      else if (!data.configured) toast.warning("Resend API key not configured — email logged as skipped_no_key");
      else toast.error("Send failed — check email logs");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  if (!isPublished) return null;

  return (
    <section data-testid="report-panel" className="rounded-3xl bg-white border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-cyan-600" />
        <h2 className="font-display text-lg font-semibold text-slate-900">Customer PDF report</h2>
        {meta && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
          meta.status === "sent" ? "bg-emerald-50 text-emerald-700" :
          meta.status === "send_attempted" ? "bg-amber-50 text-amber-700" :
          meta.status === "superseded" ? "bg-slate-100 text-slate-500" : "bg-slate-100 text-slate-700"
        }`}>{meta.status} · v{meta.version}</span>}
      </div>
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 inline-flex items-start gap-2 mb-3">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          <span className="font-semibold block">Customer Summary</span>
          The customer-facing summary is generated using the approved Pristine Water Systems template and deterministic recommendation logic. No AI-generated summary approval is required.
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Branded PDF combining the Aware Certificate of Analysis and the Pristine Water Test Summary &amp; Action Plan.</p>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="report-generate" onClick={generate} disabled={busy} className="btn-aqua rounded-full font-semibold">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : (meta ? <RefreshCw className="w-4 h-4 mr-1.5" /> : <FileText className="w-4 h-4 mr-1.5" />)}
          {meta ? "Regenerate Customer Report" : "Generate Customer Report"}
        </Button>
        {meta && (
          <>
            <Button data-testid="report-preview" variant="outline" onClick={downloadOrPreview} className="rounded-full font-semibold">
              <Eye className="w-4 h-4 mr-1.5" /> Preview Customer Report
            </Button>
            {isManager && (
              <Button data-testid="report-send" onClick={() => send()} disabled={busy} className="rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700">
                <Send className="w-4 h-4 mr-1.5" /> Send report email
              </Button>
            )}
          </>
        )}
      </div>
      {meta?.sent_at && (
        <p className="mt-3 text-[11px] text-slate-500">Last sent {formatDateTimeAU(meta.sent_at)} by <span className="font-mono">{meta.sent_by}</span></p>
      )}
    </section>
  );
}
